import * as Yup from "yup";
import { getOpportunityDateOrderErrors, isHttpImageUrl } from "./opportunityDateValidation";

const dateOrderTest = {
  name: "opportunity-date-order",
  test(value, ctx) {
    const errors = getOpportunityDateOrderErrors(ctx.parent);
    const field = ctx.path;
    if (errors[field]) {
      return ctx.createError({ message: errors[field] });
    }
    return true;
  },
};

export const stepSchemas = [
  // 0 — Basic Information
  Yup.object({
    title: Yup.string().trim().required("Title is required"),
    category: Yup.string().trim().required("Category is required"),
    providerName: Yup.string().trim().required("Provider is required"),
    riskLevel: Yup.string().required("Risk level is required"),
  }),
  // 1 — Financial Terms
  Yup.object({
    unitPriceNgn: Yup.number()
      .typeError("Unit price is required")
      .min(1, "Unit price must be at least 1")
      .required("Unit price is required"),
    expectedReturnPct: Yup.number()
      .typeError("Expected return is required")
      .min(0, "Expected return cannot be negative")
      .required("Expected return is required"),
    durationMonths: Yup.number()
      .typeError("Duration is required")
      .min(1, "Duration must be at least 1 month")
      .required("Duration is required"),
    totalUnits: Yup.number()
      .typeError("Total units is required")
      .min(1, "Total units must be at least 1")
      .required("Total units is required")
      .test(
        "total-gte-min",
        "Total units must be at least the minimum units per request",
        function (val) {
          const min = Number(this.parent.minUnits);
          return !val || !min || Number(val) >= min;
        }
      )
      .test(
        "total-gte-max",
        "Total units must be at least the maximum units per request",
        function (val) {
          const max = Number(this.parent.maxUnits);
          return !val || !max || Number(val) >= max;
        }
      ),
    minUnits: Yup.number()
      .typeError("Minimum units is required")
      .min(1, "Minimum units must be at least 1")
      .required("Minimum units is required"),
    maxUnits: Yup.number()
      .typeError("Maximum units is required")
      .min(1, "Maximum units must be at least 1")
      .required("Maximum units is required")
      .test("max-gte-min", "Maximum units must be at least minimum units", function (val) {
        return !val || !this.parent.minUnits || Number(val) >= Number(this.parent.minUnits);
      }),
    payoutType: Yup.string().required("Payout type is required"),
  }),
  // 2 — Dates & Lifecycle
  Yup.object({
    fundingOpenDate: Yup.string().required("Funding open date is required").test(dateOrderTest),
    fundingCloseDate: Yup.string().required("Funding close date is required").test(dateOrderTest),
    scheduledPublishAt: Yup.string().nullable().test(dateOrderTest),
    investmentStartDate: Yup.string()
      .required("Investment start date is required")
      .test(dateOrderTest),
    maturityDate: Yup.string().required("Maturity date is required").test(dateOrderTest),
    returnPayoutDate: Yup.string().required("Payout date is required").test(dateOrderTest),
  }),
  // 3 — Public Content (draft-friendly: overview recommended)
  Yup.object({
    overview: Yup.string().trim().required("Overview is required for public content"),
  }),
  // 4 — Media & Documents
  Yup.object({
    coverImageUrl: Yup.string()
      .trim()
      .required("Cover image is required")
      .test("http-url", "Enter a valid HTTP or HTTPS image URL", (v) => isHttpImageUrl(v)),
  }),
  // 5 — Review (full readiness checked separately)
  Yup.object({}),
];

/** Full schema for publish / submit for review */
export const publishReadySchema = Yup.object({
  title: Yup.string().trim().required(),
  category: Yup.string().trim().required(),
  providerName: Yup.string().trim().required(),
  unitPriceNgn: Yup.number().min(1).required(),
  expectedReturnPct: Yup.number().min(0).required(),
  durationMonths: Yup.number().min(1).required(),
  totalUnits: Yup.number().min(1).required(),
  fundingOpenDate: Yup.string().required().test(dateOrderTest),
  fundingCloseDate: Yup.string().required().test(dateOrderTest),
  investmentStartDate: Yup.string().required().test(dateOrderTest),
  maturityDate: Yup.string().required().test(dateOrderTest),
  returnPayoutDate: Yup.string().required().test(dateOrderTest),
  scheduledPublishAt: Yup.string().nullable().test(dateOrderTest),
  overview: Yup.string().trim().required(),
  coverImageUrl: Yup.string()
    .trim()
    .required()
    .test("http-url", "Enter a valid HTTP or HTTPS image URL", (v) => isHttpImageUrl(v)),
});

export function getReviewChecklist(values = {}) {
  const dateErrors = getOpportunityDateOrderErrors(values);
  const datesOk =
    Boolean(values.fundingOpenDate) &&
    Boolean(values.fundingCloseDate) &&
    Boolean(values.investmentStartDate) &&
    Boolean(values.maturityDate) &&
    Boolean(values.returnPayoutDate) &&
    Object.keys(dateErrors).length === 0;

  const basicOk = Boolean(
    values.title?.trim() && values.category?.trim() && values.providerName?.trim()
  );
  const financialOk =
    Number(values.unitPriceNgn) >= 1 &&
    Number(values.totalUnits) >= 1 &&
    values.expectedReturnPct !== "" &&
    values.expectedReturnPct != null &&
    Number(values.durationMonths) >= 1;
  const coverOk = isHttpImageUrl(values.coverImageUrl);
  const contentOk = Boolean(values.overview?.trim());

  const items = [
    { key: "basic", label: "Basic information complete", ok: basicOk, step: 0, blocking: true },
    { key: "financial", label: "Financial terms valid", ok: financialOk, step: 1, blocking: true },
    { key: "dates", label: "Dates valid (maturity & payout not in the past)", ok: datesOk, step: 2, blocking: true },
    { key: "cover", label: "Cover image available", ok: coverOk, step: 4, blocking: true },
    {
      key: "content",
      label: "Required public content complete",
      ok: contentOk,
      step: 3,
      blocking: true,
    },
    {
      key: "highlights",
      label: "Highlights added (optional)",
      ok: (values.highlights?.length || 0) > 0,
      step: 3,
      blocking: false,
    },
    {
      key: "faqs",
      label: "FAQs added (optional)",
      ok: (values.faqs?.length || 0) > 0,
      step: 3,
      blocking: false,
    },
    {
      key: "disclaimer",
      label: "Risk disclaimer present (optional)",
      ok: Boolean(values.riskDisclaimer?.trim()),
      step: 3,
      blocking: false,
    },
  ];

  const ready = items.filter((i) => i.blocking).every((i) => i.ok);
  items.push({
    key: "ready",
    label: ready ? "Opportunity is ready for review" : "Opportunity is not ready for review yet",
    ok: ready,
    step: 5,
    blocking: true,
  });

  return { items, ready, dateErrors };
}

/** Derive which steps are already satisfied from saved opportunity data */
export function deriveCompletedSteps(values = {}) {
  const completed = [];
  for (let i = 0; i < stepSchemas.length - 1; i += 1) {
    try {
      stepSchemas[i].validateSync(values, { abortEarly: false });
      completed.push(i);
    } catch {
      break;
    }
  }
  return completed;
}
